// El hilo de una consulta del propietario y el formulario de respuesta
// (ADR-0145, #0738). Primer consumidor real de `.drawer--hilo`: el drawer
// ancho con hilo que el design pull de 2026-08-12 dejó listo.
//
// Reglas:
//   - El hilo es cronológico (el precedente del dashboard, el historial
//     de gestión de una observación; la PWA invierte, acá no).
//   - La entrada 0 es la consulta misma (autor: el propietario); las
//     demás son respuestas del staff o, desde ADR-0155, mensajes del
//     propietario (`seguimiento`). Los nombres se resuelven contra el
//     roster de GET /auth/memberships (ADR-0085, opción C); el rol sale de
//     la receta.
//   - «N nuevos» se pinta con la marca PREVIA que trae el detalle
//     (`lastReadAt`); la marca se avanza DESPUÉS del primer render con el
//     `createdAt` de la última entrada, nunca con el reloj del cliente.
//   - La clave de idempotencia va atada al texto: un reintento del mismo
//     texto reutiliza la clave (el servidor devuelve el mismo mensaje sin
//     reenviar el correo); si el texto cambia, clave nueva.
//   - El correo al propietario es best-effort en el servidor: con
//     `ownerNotified === false` la respuesta igual quedó guardada y acá
//     sólo se avisa, sin bloquear.
//
// ADR-0158 (#0760) — casi en vivo y con archivos:
//   - Con el drawer abierto y la pestaña visible el hilo se revisa cada
//     15 s (useOwnerInquiry). El separador «N nuevos» usa la marca de la
//     PRIMERA carga traída del servidor en esta apertura (no la del caché:
//     al reabrir, el caché trae la marca de antes de la lectura anterior)
//     y queda fijo; lo que llega se anuncia en una región live, sin mover
//     la vista. Un error de una revisión de fondo no tapa el hilo que ya
//     se veía ni bloquea el campo.
//   - Cada mensaje pinta sus fotos (visor) y PDF («Abrir»). El visor se
//     monta en <body> (portal): el Escape nativo del <dialog> no llega al
//     listener de teclado del drawer, así cierra sólo el visor.
//   - El jefe/gerente adjunta PDF (hasta 5, 10 MB c/u) a su respuesta; un
//     mensaje puede ir sólo con PDF. Se suben al enviar y un reintento con
//     el mismo contenido no los vuelve a subir. Si el servidor dice que una
//     clave ya está en otro mensaje (el envío anterior se guardó y su
//     respuesta se perdió), lo subido se olvida y el próximo envío sube de
//     nuevo: si no, cada reintento chocaría con el mismo 409.
//
// ADR-0163 (#0770) — convertir en observación:
//   - Con `canConvert`, el lead ofrece «Convertir en observación», que
//     cambia este mismo drawer a la vista «convertir» (ConvertirConsultaForm;
//     no anida otro drawer). Mientras crea, Escape y la X no cierran.
//   - Una consulta convertida queda SÓLO PARA LEER: el lead dice «Pasó a
//     observación {folio}» con el enlace, y el formulario de respuesta se
//     reemplaza por una nota. Si alguien la convirtió mientras se escribía,
//     el 409 INQUIRY_CONVERTED de la respuesta vuelve a pedir el hilo.

import { useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  consultaPasoAObservacionLabel,
  OWNER_INQUIRY_ATTACHMENTS_PER_MESSAGE_MAX,
  OWNER_INQUIRY_BODY_MAX,
  OWNER_INQUIRY_CONVERTED,
  type OrgMember,
  type OwnerInquiryActor,
  type OwnerInquiryEntry,
} from "@bitacorapro/api-types";
import { AdjuntosMensaje } from "./AdjuntosMensaje";
import { ConvertirConsultaForm } from "./ConvertirConsultaForm";
import { Drawer } from "./Drawer";
import { Lightbox, type LightboxPhoto } from "./Lightbox";
import { PageSkeleton } from "./PageSkeleton";
import { Skel } from "./Skel";
import { putToStorage } from "../lib/api/endpoints/documents";
import { getOwnerInquiryAttachmentUploadUrl } from "../lib/api/endpoints/owner-inquiries";
import { ApiError } from "../lib/api/error";
import { mensajeDeError } from "../lib/api/error-message";
import {
  queryKeys,
  useMarkOwnerInquiryRead,
  useMemberships,
  useOwnerInquiry,
  useRecipe,
  useReplyOwnerInquiry,
} from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { useQueryView } from "../lib/loading/useQueryView";
import { fileProblem } from "../routes/DocumentosProyecto";
import {
  claveDeArchivoInservible,
  contadorCaracteres,
  estadoLabel,
  ESTADO_TONE,
  esReabierta,
  nuevosLabel,
  plazoRespuesta,
  primeraNueva,
  textoAnuncio,
  ultimaEntradaAt,
} from "../lib/postventa/consultasList";
import { describeTimelineActor, lastSeenLabel } from "../lib/util";
import { useToast } from "../lib/useToast";

const TONE_PILL: Record<string, string> = {
  ok: "st-pill--ok",
  info: "st-pill--info",
  warn: "st-pill--warn",
  crit: "st-pill--crit",
  idle: "st-pill--idle",
  // ADR-0163 — «Pasó a observación».
  brand: "st-pill--brand",
};

const FALLBACK_ROLE_LABELS: Record<string, string> = {
  propietario: "Propietario",
  jefe_proyecto: "Jefe de proyecto",
  gerente_postventa: "Gerente de postventa",
  admin: "Administrador",
};

export const REPLY_ERROR_FALLBACK = "No pudimos enviar tu respuesta. Intenta de nuevo.";
export const UPLOAD_ERROR_FALLBACK = "No pudimos subir el PDF. Intenta de nuevo.";
export const ATTACHMENTS_LIMIT = `Puedes adjuntar hasta ${OWNER_INQUIRY_ATTACHMENTS_PER_MESSAGE_MAX} archivos.`;

const PDF = "application/pdf";
const MB = 1024 * 1024;
const fileKey = (f: File) => JSON.stringify([f.name, f.size, f.lastModified]);
const peso = (bytes: number) =>
  bytes >= MB
    ? `${(bytes / MB).toLocaleString("es-CL", { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024)).toLocaleString("es-CL")} KB`;
export const OWNER_NOT_NOTIFIED =
  "La respuesta quedó guardada, pero no pudimos avisarle al propietario por correo. La verá al entrar a la app.";
/** ADR-0163 — la conversión salió pero el correo al propietario no. */
export const CONVERSION_OWNER_NOT_NOTIFIED =
  "La observación quedó creada, pero no pudimos avisarle al propietario por correo. La verá al entrar a la app.";

/** El toast de una conversión recién creada (sin dos espacios si aún no hay
 *  folio). */
export function toastConversion(res: {
  conversion: { folio: string | null };
  ownerNotified: boolean;
  ownerNotice?: string | undefined;
}): string {
  const obs = res.conversion.folio
    ? `Observación ${res.conversion.folio} creada`
    : "Observación creada";
  if (res.ownerNotified) return `${obs} · se avisó al propietario`;
  if (res.ownerNotice === "suppressed_by_preference") {
    return `${obs}. El propietario eligió no recibir este aviso por correo; la verá en la app.`;
  }
  return obs;
}

type AutorPintado = { nombre: string; rol: string; rolClass: string };

/** El rótulo de cada entrada del hilo. */
const KIND_LABEL: Record<OwnerInquiryEntry["kind"], string> = {
  consulta: "Consulta",
  seguimiento: "Mensaje del propietario",
  respuesta: "Respuesta",
};

export function ConsultaDrawer({ inquiryId, onClose }: { inquiryId: string; onClose: () => void }) {
  const detail = useOwnerInquiry(inquiryId);
  // ADR-0166: el hilo pasa por el contrato de carga (skeleton con umbral,
  // error que no vuelve a skeleton en la revisión de fondo de 15 s).
  const detailView = useQueryView(detail);
  const { data: recipe } = useRecipe();
  const session = useSession();
  const memberships = useMemberships();
  const reply = useReplyOwnerInquiry();
  const markRead = useMarkOwnerInquiryRead();
  const toast = useToast();
  const qc = useQueryClient();

  const [texto, setTexto] = useState("");
  // ADR-0158 — PDF de la respuesta y el visor de fotos.
  const [files, setFiles] = useState<File[]>([]);
  const [subiendo, setSubiendo] = useState(false);
  const [errorSubida, setErrorSubida] = useState<string | null>(null);
  const [visor, setVisor] = useState<{
    fotos: LightboxPhoto[];
    index: number;
    refirmar: () => Promise<LightboxPhoto[]>;
  } | null>(null);
  // Una foto que no carga en el visor (URL vencida) se re-firma una sola
  // vez por apertura: si vuelve a fallar, no hay bucle.
  const visorRefirmadoRef = useRef(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const fileFieldId = useId();
  const fileHintId = useId();
  const [sinAviso, setSinAviso] = useState(false);
  // ADR-0163 — la vista del drawer y si la conversión está en vuelo.
  const [vista, setVista] = useState<"hilo" | "convertir">("hilo");
  const [convirtiendo, setConvirtiendo] = useState(false);
  // El correo de la conversión falló: el aviso queda a la vista en el pie.
  const [conversionSinAviso, setConversionSinAviso] = useState(false);
  // A dónde vuelve el foco al salir de la vista «convertir»: al botón (con
  // «Volver») o al aviso de la consulta cerrada (tras convertir). Sin esto
  // el foco caía al body, fuera del drawer modal.
  const [focoAlVolver, setFocoAlVolver] = useState<"boton" | "aviso" | null>(null);
  const convertirBtnRef = useRef<HTMLButtonElement>(null);
  const avisoConvertidaRef = useRef<HTMLParagraphElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fieldId = useId();
  const hintId = useId();
  const nuevosId = useId();
  // El intento en curso: la clave ligada al texto Y a los archivos (la
  // misma mientras no cambien) y lo ya subido de cada archivo, que un
  // reintento no vuelve a subir. Sólo un éxito lo libera.
  const attemptRef = useRef<{
    firma: string;
    key: string;
    objectKeys: Map<string, string>;
  } | null>(null);
  // La marca se avanza una sola vez por valor; sin esto, cada refetch del
  // detalle (p. ej. tras responder) volvería a mandar la misma marca.
  const marcadoRef = useRef<string | null>(null);

  const rosterByUserId = useMemo(
    () =>
      new Map<string, Pick<OrgMember, "displayName" | "role">>(
        (memberships.data?.members ?? []).map((m) => [m.userId, m])
      ),
    [memberships.data]
  );
  const selfUserId = session?.user.id ?? null;
  const selfRole = session?.organization.role ?? null;

  const roleLabel = (key: string | null | undefined): string | null => {
    if (!key) return null;
    const fromRecipe = recipe?.memberRoles.find((r) => r.key === key)?.label;
    if (fromRecipe) return fromRecipe.replace(/\s*·\s*legacy$/i, "");
    return FALLBACK_ROLE_LABELS[key] ?? null;
  };

  /** Autor de una entrada: nombre del roster (o «Tú»), pill de rol. La
   *  consulta y los mensajes del propietario (`seguimiento`, ADR-0155) son
   *  suyos aunque el roster no lo conozca: nunca caen al nombre de la
   *  empresa. */
  function autorDe(entry: OwnerInquiryEntry): AutorPintado {
    const actor: OwnerInquiryActor = entry.author;
    const userId = actor.kind === "user" ? actor.userId : null;
    const esYo = userId !== null && userId === selfUserId;
    const memberRole = esYo ? selfRole : userId ? (rosterByUserId.get(userId)?.role ?? null) : null;
    if (entry.kind === "consulta" || entry.kind === "seguimiento") {
      const nombre =
        userId && rosterByUserId.has(userId)
          ? describeTimelineActor(actor, rosterByUserId)
          : (roleLabel("propietario") ?? "Propietario");
      return { nombre, rol: roleLabel("propietario") ?? "Propietario", rolClass: "" };
    }
    const nombre = esYo
      ? "Tú"
      : userId && rosterByUserId.has(userId)
        ? describeTimelineActor(actor, rosterByUserId)
        : (recipe?.labels.organization ?? "Equipo");
    return { nombre, rol: roleLabel(memberRole) ?? "Equipo", rolClass: "role-pill--sup" };
  }

  const entries = useMemo(() => detail.data?.entries ?? [], [detail.data]);
  // La marca PREVIA de la primera carga de esta apertura: las revisiones
  // traen la marca ya avanzada y el separador no debe moverse ni
  // desaparecer mientras el drawer sigue abierto.
  // Sólo un dato traído en ESTA apertura: el del caché (reabrir dentro
  // de los 5 min de gc) trae la marca de antes de la lectura anterior, y
  // markRead a propósito no toca el detalle.
  const [marcaPrevia, setMarcaPrevia] = useState<string | null | undefined>(undefined);
  const datoFresco = detail.data && detail.isFetchedAfterMount ? detail.data : undefined;
  if (marcaPrevia === undefined && datoFresco) setMarcaPrevia(datoFresco.lastReadAt);
  const { index: primeraNuevaIdx, nuevos } = useMemo(
    () => primeraNueva(entries, marcaPrevia ?? null, selfUserId),
    [entries, marcaPrevia, selfUserId]
  );

  // Lo que trae una revisión: se anuncia al lector de pantalla (sin mover
  // la vista) y, si cambió el estado, se refresca el rollup de cartera.
  const [anuncio, setAnuncio] = useState("");
  const vueltaAnuncioRef = useRef(0);
  const previoRef = useRef<{ delPropietario: number; estado: string } | null>(null);
  useEffect(() => {
    if (!detail.data) return;
    const delPropietario = detail.data.entries.filter(
      (e) => e.kind === "consulta" || e.kind === "seguimiento"
    ).length;
    const estado = detail.data.inquiry.estado;
    const previo = previoRef.current;
    previoRef.current = { delPropietario, estado };
    if (!previo) return;
    if (delPropietario > previo.delPropietario) {
      vueltaAnuncioRef.current += 1;
      setAnuncio(textoAnuncio(delPropietario - previo.delPropietario, vueltaAnuncioRef.current));
    }
    if (estado !== previo.estado) {
      void qc.invalidateQueries({ queryKey: queryKeys.carteraDashboardAll });
    }
  }, [detail.data, qc]);

  // Marca de lectura después del primer render con datos: `lastReadAt`
  // = createdAt de la última entrada mostrada. Sólo si avanza.
  // Con un dato traído en esta apertura (el del caché tiene la marca vieja
  // y mandaría otra vez la misma).
  useEffect(() => {
    if (!datoFresco) return;
    const ultima = ultimaEntradaAt(datoFresco.entries);
    if (!ultima) return;
    if (datoFresco.lastReadAt !== null && ultima <= datoFresco.lastReadAt) return;
    if (marcadoRef.current === ultima) return;
    marcadoRef.current = ultima;
    markRead.mutate({ id: inquiryId, lastReadAt: ultima });
    // markRead es estable (useMutation); el efecto depende de los datos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datoFresco, inquiryId]);

  const pending = reply.isPending;
  const ocupado = pending || subiendo || convirtiendo;
  const recortado = texto.trim();
  const problemas = files.map(fileProblem).filter((p): p is string => p !== null);
  const demasiados = files.length > OWNER_INQUIRY_ATTACHMENTS_PER_MESSAGE_MAX;
  const listo =
    (recortado.length > 0 || files.length > 0) && !ocupado && problemas.length === 0 && !demasiados;

  function elegirArchivos(e: ChangeEvent<HTMLInputElement>) {
    const nuevos = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (nuevos.length === 0) return;
    setErrorSubida(null);
    setFiles((prev) => {
      const vistos = new Set(prev.map(fileKey));
      return [...prev, ...nuevos.filter((f) => !vistos.has(fileKey(f)))];
    });
  }

  function quitarArchivo(f: File) {
    setFiles((prev) => prev.filter((x) => x !== f));
  }

  async function enviar() {
    if (!listo) return;
    const firma = JSON.stringify([recortado, files.map(fileKey)]);
    const prev = attemptRef.current;
    const attempt =
      prev && prev.firma === firma
        ? prev
        : {
            firma,
            key: crypto.randomUUID(),
            // Lo ya subido sirve aunque cambie el texto: es el mismo archivo.
            objectKeys: prev?.objectKeys ?? new Map<string, string>(),
          };
    attemptRef.current = attempt;
    setErrorSubida(null);
    const attachments: { objectKey: string }[] = [];
    if (files.length > 0) {
      setSubiendo(true);
      try {
        for (const f of files) {
          const k = fileKey(f);
          let objectKey = attempt.objectKeys.get(k);
          if (!objectKey) {
            const presign = await getOwnerInquiryAttachmentUploadUrl(inquiryId, {
              contentType: PDF,
              contentLength: f.size,
            });
            // Sin tipo (algunos gestores de archivos), se re-etiqueta: la
            // extensión ya dijo que es PDF (fileProblem).
            await putToStorage(presign, f.type === PDF ? f : new Blob([f], { type: PDF }));
            objectKey = presign.objectKey;
            attempt.objectKeys.set(k, objectKey);
          }
          attachments.push({ objectKey });
        }
      } catch (err) {
        setErrorSubida(mensajeDeError(err, UPLOAD_ERROR_FALLBACK));
        // ADR-0163 — la URL de subida también responde 409 si otra persona
        // la convirtió: el hilo vuelve con la marca y sin el formulario.
        if (err instanceof ApiError && err.code === OWNER_INQUIRY_CONVERTED) {
          void detail.refetch();
        }
        textareaRef.current?.focus();
        return;
      } finally {
        setSubiendo(false);
      }
    }
    reply.mutate(
      {
        id: inquiryId,
        // Sin adjuntos, el mismo cuerpo de antes (un core-api sin ADR-0158
        // lo sigue aceptando: su schema es `.strict()`).
        body: attachments.length > 0 ? { body: recortado, attachments } : { body: recortado },
        idempotencyKey: attempt.key,
      },
      {
        onError: (err) => {
          if (claveDeArchivoInservible(err)) attemptRef.current = null;
          // ADR-0163 — otra persona la convirtió mientras se escribía: el
          // hilo vuelve con la marca y sin el formulario.
          if (err instanceof ApiError && err.code === OWNER_INQUIRY_CONVERTED) {
            void detail.refetch();
          }
        },
        onSuccess: (res) => {
          attemptRef.current = null;
          setTexto("");
          setFiles([]);
          if (res.replayed) {
            // El servidor devolvió el mensaje que ya había guardado con esta
            // clave y no reintentó el aviso: `ownerNotified: false` aquí es
            // «no se intentó», no «falló». Sin aviso falso.
            setSinAviso(false);
            toast.show("Respuesta enviada");
          } else if (res.ownerNotice === "suppressed_by_preference") {
            // ADR-0147: apagó el correo de «Consultas». No es un fallo (no
            // va el aviso «no pudimos avisar»), pero tampoco «se avisó».
            setSinAviso(false);
            toast.show(
              "Respuesta enviada. El propietario eligió no recibir este aviso por correo; la verá en la app."
            );
          } else if (res.ownerNotified) {
            setSinAviso(false);
            toast.show("Respuesta enviada · se avisó al propietario");
          } else {
            setSinAviso(true);
          }
        },
        // Tras éxito o error el foco vuelve al campo. En vuelo el textarea
        // es readOnly (no disabled): no suelta el foco y `focus()` aquí no
        // es un no-op sobre un control deshabilitado.
        onSettled: () => {
          textareaRef.current?.focus();
        },
      }
    );
  }

  // Escape y la X cierran sólo si no hay una respuesta en vuelo: cerrar a
  // mitad del envío dejaría al jefe sin saber si salió.
  function cerrar() {
    if (ocupado) return;
    onClose();
  }

  const inquiry = detail.data?.inquiry;
  const now = useMemo(() => new Date(), [detail.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const plazo = inquiry ? plazoRespuesta(inquiry, now) : null;
  const conversion = inquiry?.conversion ?? null;
  const puedeConvertir = detail.data?.canConvert === true && conversion === null;

  useEffect(() => {
    if (vista !== "hilo" || focoAlVolver === null) return;
    const destino = focoAlVolver === "aviso" ? avisoConvertidaRef.current : convertirBtnRef.current;
    if (!destino) return;
    destino.focus();
    setFocoAlVolver(null);
  }, [vista, focoAlVolver, conversion]);

  const lead =
    inquiry && conversion ? (
      <div className="drawer__lead-chips">
        <span className={`st-pill ${TONE_PILL.brand}`}>
          {consultaPasoAObservacionLabel(conversion.folio)}
        </span>
        {inquiry.assetGroupId && (
          <Link
            className="btn btn--ghost btn--sm"
            to={`/proyectos/${inquiry.assetGroupId}/observaciones/${conversion.findingId}`}
          >
            Ver observación
          </Link>
        )}
        <span>Recibida {lastSeenLabel(inquiry.createdAt).toLowerCase()}</span>
      </div>
    ) : inquiry ? (
      <div className="drawer__lead-chips">
        <span className={`st-pill ${TONE_PILL[ESTADO_TONE[inquiry.estado]]}`}>
          {estadoLabel(inquiry.estado)}
        </span>
        {esReabierta(inquiry) && (
          <span className={`st-pill ${TONE_PILL.info}`}>Nuevo mensaje del propietario</span>
        )}
        {plazo && inquiry.estado !== "respondida" && (
          <span className={`st-pill ${TONE_PILL[plazo.tone]}`}>{plazo.texto}</span>
        )}
        <span>Recibida {lastSeenLabel(inquiry.createdAt).toLowerCase()}</span>
        {puedeConvertir && vista === "hilo" && (
          <button
            ref={convertirBtnRef}
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={ocupado}
            onClick={() => setVista("convertir")}
          >
            Convertir en observación
          </button>
        )}
      </div>
    ) : null;

  const footer = conversion ? (
    <>
      <p className="consulta-reply__aviso" ref={avisoConvertidaRef} tabIndex={-1}>
        {conversion.folio
          ? `Esta consulta pasó a la observación ${conversion.folio}.`
          : "Esta consulta pasó a observación."}{" "}
        Ya no se puede responder aquí; el propietario la sigue desde la observación.
      </p>
      <p className="consulta-reply__aviso" role="status" aria-live="polite">
        {conversionSinAviso ? CONVERSION_OWNER_NOT_NOTIFIED : ""}
      </p>
    </>
  ) : vista === "convertir" ? null : (
    <form
      className="consulta-reply"
      onSubmit={(e) => {
        e.preventDefault();
        void enviar();
      }}
    >
      <div className="field">
        <label className="field__label" htmlFor={fieldId}>
          Tu respuesta
        </label>
        <textarea
          id={fieldId}
          ref={textareaRef}
          className="textarea"
          rows={4}
          maxLength={OWNER_INQUIRY_BODY_MAX}
          value={texto}
          readOnly={ocupado}
          aria-busy={ocupado || undefined}
          disabled={detail.isError && !detail.data}
          aria-describedby={hintId}
          onChange={(e) => setTexto(e.target.value)}
        />
        <span className="field__hint" id={hintId}>
          {contadorCaracteres(texto.length, OWNER_INQUIRY_BODY_MAX)} · El propietario la verá en la
          app, recibirá un aviso por correo y podrá responderte desde la app. No se puede editar
          después.
        </span>
      </div>
      <div className="field">
        <label className="field__label" htmlFor={fileFieldId}>
          Adjuntar PDF (opcional)
        </label>
        <input
          id={fileFieldId}
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          disabled={ocupado || (detail.isError && !detail.data)}
          onChange={elegirArchivos}
          aria-describedby={fileHintId}
        />
        <span className="field__hint" id={fileHintId}>
          Puedes adjuntar hasta {OWNER_INQUIRY_ATTACHMENTS_PER_MESSAGE_MAX} PDF de hasta 10 MB.
          Puedes enviar sólo el PDF, sin texto.
        </span>
        {files.length > 0 && (
          <ul className="consulta-reply__files">
            {files.map((f) => {
              const problema = fileProblem(f);
              return (
                <li key={fileKey(f)} className="consulta-reply__file">
                  <span className="consulta-reply__file-name">
                    {f.name} · {peso(f.size)}
                  </span>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={ocupado}
                    onClick={() => quitarArchivo(f)}
                  >
                    Quitar<span className="sr-only"> {f.name}</span>
                  </button>
                  {problema && (
                    <p className="auth-error" role="alert">
                      {problema}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {demasiados && (
          <p className="auth-error" role="alert">
            {ATTACHMENTS_LIMIT}
          </p>
        )}
      </div>
      {errorSubida && (
        <p className="auth-error" role="alert">
          {errorSubida}
        </p>
      )}
      {reply.isError && (
        <p className="auth-error" role="alert">
          {mensajeDeError(reply.error, REPLY_ERROR_FALLBACK)}
        </p>
      )}
      <p className="consulta-reply__aviso" role="status" aria-live="polite">
        {sinAviso ? OWNER_NOT_NOTIFIED : ""}
      </p>
      <div className="consulta-reply__actions">
        <button type="submit" className="btn btn--primary" disabled={!listo}>
          {subiendo ? "Subiendo PDF…" : pending ? "Enviando…" : "Enviar respuesta"}
        </button>
      </div>
    </form>
  );

  return (
    <Drawer
      className="drawer--hilo"
      title={inquiry?.propiedadLabel ?? "Consulta"}
      lead={lead}
      onClose={cerrar}
      initialFocusRef={textareaRef}
      footer={footer}
    >
      {vista === "convertir" && detail.data && !conversion ? (
        <ConvertirConsultaForm
          inquiryId={inquiryId}
          detail={detail.data}
          canSuggest={detail.data.canSuggest === true}
          onVolver={() => {
            setFocoAlVolver("boton");
            setVista("hilo");
          }}
          onOcupado={setConvirtiendo}
          onConvertida={(res) => {
            setConvirtiendo(false);
            // Un replay no reintenta el correo: `ownerNotified: false` ahí es
            // «no se intentó», no «falló» (como en la respuesta).
            setConversionSinAviso(!res.replayed && res.ownerNotice === "failed");
            setFocoAlVolver("aviso");
            setVista("hilo");
            toast.show(toastConversion(res));
          }}
        />
      ) : detailView.kind === "loading" ? (
        <PageSkeleton label="Cargando la consulta…" visible={detailView.visible}>
          <div className="skel-row" aria-hidden="true">
            <Skel width="40%" height={12} />
            <Skel width="100%" height={48} />
            <Skel width="70%" height={12} />
          </div>
        </PageSkeleton>
      ) : detailView.kind === "error" ? (
        <p className="auth-error" role="alert">
          {mensajeDeError(
            detailView.error,
            "No pudimos cargar la consulta. Cierra e intenta de nuevo."
          )}
        </p>
      ) : (
        <section aria-labelledby={nuevosId}>
          <div className="drawer__section-label" id={nuevosId}>
            Hilo
          </div>
          <ol className="hilo">
            {entries.map((e, i) => {
              const autor = autorDe(e);
              const tone = e.kind === "respuesta" ? "ev--assign" : "ev--open";
              return [
                i === primeraNuevaIdx && nuevos > 0 ? (
                  <li key={`sep-${e.id}`} className="hilo__sep" aria-label={nuevosLabel(nuevos)}>
                    <span className="hilo__sep-pill">{nuevosLabel(nuevos)}</span>
                  </li>
                ) : null,
                <li key={e.id} className={`ev ${tone}`}>
                  <span className="ev__dot" aria-hidden="true" />
                  <div className="ev__card">
                    <div className="ev__head">
                      <span className="ev__author">{autor.nombre}</span>
                      <span className={`role-pill ${autor.rolClass}`}>{autor.rol}</span>
                      <time className="ev__time" dateTime={e.createdAt}>
                        {lastSeenLabel(e.createdAt)}
                      </time>
                    </div>
                    <div className="ev__kind">{KIND_LABEL[e.kind]}</div>
                    {e.body && <p className="ev__text">{e.body}</p>}
                    <AdjuntosMensaje
                      inquiryId={inquiryId}
                      attachments={e.attachments}
                      onVerFoto={(fotos, index, refirmar) => {
                        visorRefirmadoRef.current = false;
                        setVisor({ fotos, index, refirmar });
                      }}
                    />
                  </div>
                </li>,
              ];
            })}
          </ol>
          <p className="sr-only" role="status" aria-live="polite">
            {anuncio}
          </p>
        </section>
      )}
      {createPortal(
        <Lightbox
          photos={visor?.fotos ?? []}
          openIndex={visor ? visor.index : null}
          onClose={() => setVisor(null)}
          onNavigate={(index) => setVisor((v) => (v ? { ...v, index } : v))}
          ariaLabel="Foto adjunta a la consulta"
          onImageError={() => {
            if (!visor || visorRefirmadoRef.current) return;
            visorRefirmadoRef.current = true;
            void visor
              .refirmar()
              .then((fotos) => setVisor((v) => (v && fotos.length > 0 ? { ...v, fotos } : v)))
              .catch(() => {});
          }}
        />,
        document.body
      )}
    </Drawer>
  );
}
